import React from "react";
import { Descriptions, Divider, Select, Space } from "antd";
import { hasValue } from "./quoteVersionUtils";
import { PendingChangeNote } from "./PendingChangeNote";
import { CoverOptionsResponse } from "../../../interfaces/OrderDetail";

interface PoolCoverSpecsProps {
  poolDimensions: string | null;
  coverDimensions: string | null;
  straps: number | null;
  color: string | null;
  spacing: string | null;
  overlap: string | null;
  productLine: string | null;
  material: string | null;
  // controlled edit props — only needed when editing
  editing?: boolean;
  options?: CoverOptionsResponse | null;
  selectedColorId?: number | null;
  selectedMaterialId?: number | null;
  onColorChange?: (id: number) => void;
  onMaterialChange?: (id: number) => void;
}

export const PoolCoverSpecs: React.FC<PoolCoverSpecsProps> = ({
  poolDimensions,
  coverDimensions,
  straps,
  color,
  spacing,
  overlap,
  productLine,
  material,
  editing = false,
  options,
  selectedColorId,
  selectedMaterialId,
  onColorChange,
  onMaterialChange,
}) => {
  const hasPoolSpecs =
    hasValue(poolDimensions) ||
    hasValue(coverDimensions) ||
    hasValue(straps) ||
    hasValue(color) ||
    hasValue(spacing) ||
    hasValue(overlap) ||
    hasValue(productLine) ||
    hasValue(material);

  if (!hasPoolSpecs) return null;

  const hasColorChanges =
    (options?.colors.find((c) => c.id === selectedColorId)?.descrip ?? "")
      .trim()
      .toLowerCase() !== (color ?? "").trim().toLowerCase();

  const hasMaterialChanges =
    (options?.materials.find((m) => m.id === selectedMaterialId)?.descrip ?? "")
      .trim()
      .toLowerCase() !== (material ?? "").trim().toLowerCase();

  const isStockCover = productLine === "Stock Cover";

  return (
    <Space direction="vertical" style={{ display: "flex" }}>
      <Divider orientation="left">Cover Details</Divider>
      <Descriptions size="small" bordered column={2} style={{ marginBottom: 12 }}>
        {hasValue(poolDimensions) && (
          <Descriptions.Item label="Pool">{poolDimensions}</Descriptions.Item>
        )}
        {hasValue(straps) && (
          <Descriptions.Item label="Straps">{straps}</Descriptions.Item>
        )}
        {hasValue(coverDimensions) && (
          <Descriptions.Item label="Cover">{coverDimensions}</Descriptions.Item>
        )}
        {hasValue(overlap) && (
          <Descriptions.Item label="Overlap">{overlap}</Descriptions.Item>
        )}
        {hasValue(productLine) && (
          <Descriptions.Item label="Product Line">{productLine}</Descriptions.Item>
        )}
        {hasValue(spacing) && (
          <Descriptions.Item label="Spacing">{spacing}</Descriptions.Item>
        )}
        {(hasValue(color) || editing) && (
          <Descriptions.Item label="Color">
            {editing && options && !isStockCover ? (
              <Space direction="vertical">
                <Select
                  value={selectedColorId ?? undefined}
                  onChange={onColorChange}
                  style={{ width: 150 }}
                  options={options.colors.map((c) => ({
                    value: c.id,
                    label: c.descrip,
                  }))}
                />
                {hasColorChanges && <PendingChangeNote />}
              </Space>
            ) : (
              color
            )}
          </Descriptions.Item>
        )}
        {(hasValue(material) || editing) && (
          <Descriptions.Item label="Material">
            {editing && options && !isStockCover ? (
              <Space direction="vertical">
                <Select
                  value={selectedMaterialId ?? undefined}
                  onChange={onMaterialChange}
                  style={{ width: 200 }}
                  options={options.materials.map((m) => ({
                    value: m.id,
                    label: m.descrip,
                  }))}
                />
                {hasMaterialChanges && <PendingChangeNote />}
              </Space>
            ) : (
              material
            )}
          </Descriptions.Item>
        )}
      </Descriptions>
    </Space>
  );
};
